import React, { useState, useMemo } from 'react';
import { compose } from 'recompose';
import { connect } from 'react-redux';
import { withStyles } from '@material-ui/core/styles';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Typography,
  Grow,
  Tooltip,
  Collapse,
  Popover
} from '@material-ui/core';
import ExpandMoreIcon from "@material-ui/icons/ExpandMore";
import { appNotify } from 'store/actions';
import ClearIcon from '@material-ui/icons/Clear';
import { DataGrid, Column } from 'devextreme-react/data-grid';
import classNames from 'classnames';
import isPlainObject from 'lodash/isPlainObject';

const ReloadStatusModal = ({ classes, open, handleClose, txnOppId, selectedOpportunityReloadStatusError, selectedOpportunityReloadStatusMessage, selectedOpportunityReloadStatusFetchMessage, selectedOpportunityReloadStatusData }) => {
  const getStatusColor = status => {
    switch (status) {
      case 'Fresh':
        return classes.fresh;
      case 'Stale':
        return classes.stale;
      case 'Error':
        return classes.error;
      case 'Unknown':
      default:
        return null;
    }
  }

  const ReloadStatusCategories = () => {
    if (!selectedOpportunityReloadStatusData) return null;
    return selectedOpportunityReloadStatusData.map(category => (
      <ReloadStatusExpander key={category.categoryName} category={category} />
    ));
  }

  const ReloadStatusExpander = ({ category }) => {
    const [isExpanded, setIsExpanded] = useState(category.status === 'Stale');

    const hasContent = category.errorMessage || category.message || (category.fieldStatuses && category.fieldStatuses.length > 0);

    const LatestValueCell = (el) => {
      const [anchorEl, setAnchorEl] = useState(null);
      const value = el.data.value;

      if (value === null) {
        return <div>-</div>;
      }

      if (isPlainObject(el.data.value)) {
        return (
          <div>
            <button className={classes.link} onClick={e => setAnchorEl(e.currentTarget)}>Data</button>
            <Popover
              key='appMessagesPopover'
              open={Boolean(anchorEl)}
              anchorEl={anchorEl}
              onClose={() => setAnchorEl(null)}
              anchorOrigin={{
                vertical: 'bottom',
                horizontal: 'center',
              }}
              transformOrigin={{
                vertical: 'top',
                horizontal: 'center',
              }}
            >
              <div style={{ whiteSpace: 'pre', padding: 16, maxHeight: 600, maxWidth: 600, overflow: 'auto' }}>
                <Typography variant='caption'>
                  {JSON.stringify(el.data.value, null, 4)}
                </Typography>
              </div>
            </Popover>
          </div>
        );
      }
      
      if (typeof value === 'number') {
        // handle NaN / Infinity gracefully
        if (!Number.isFinite(value)) {
          return <div title={String(value)}>{String(value)}</div>;
        }

        const abs = Math.abs(value);

        // If value is effectively an integer, show as integer (no decimals)
        if (Math.abs(value - Math.round(value)) < 1e-9) {
          return (
            <div title={String(value)}>
              {new Intl.NumberFormat(undefined, { maximumFractionDigits: 0 }).format(Math.round(value))}
            </div>
          );
        }

        // Very small numbers -> scientific
        if (abs > 0 && abs < 1e-4) {
          return <div title={String(value)}>{value.toExponential(2)}</div>;
        }

        // Fractions < 1 -> use significant digits (good for percents/ratios)
        if (abs < 1) {
          return (
            <div title={String(value)}>
              {new Intl.NumberFormat(undefined, { maximumSignificantDigits: 6 }).format(value)}
            </div>
          );
        }

        // 1 <= abs < 100 -> 3 decimal places
        if (abs >= 1 && abs < 100) {
          return (
            <div title={String(value)}>
              {new Intl.NumberFormat(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 3, useGrouping: true }).format(value)}
            </div>
          );
        }

        // 100 <= abs < 1000 -> 1 decimal place
        if (abs >= 100 && abs < 1000) {
          return (
            <div title={String(value)}>
              {new Intl.NumberFormat(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 1, useGrouping: true }).format(value)}
            </div>
          );
        }

        // abs >= 1000 -> no decimals, use grouping
        return (
          <div title={String(value)}>
            {new Intl.NumberFormat(undefined, { maximumFractionDigits: 0, useGrouping: true }).format(value)}
          </div>
        );
      }
      
      return <div>{value.toString()}</div>;
    }
    
    const handleIsStaleOnRowPrepared = (row) => {        
        if (row.rowType === 'data' && row.data.isStale) {
            row.rowElement.style.backgroundColor = '#fff6e7';
        }
    }

    return (
      <div className={classes.layerContainer}>
        <div className={classNames(classes.header, getStatusColor(category.status))}>
          <div className={classes.headerLeft}>
            <IconButton
              className={classes.expandButton}
              style={{transform: `rotate(${isExpanded ? '0' : '-90'}deg)`,}}
              onClick={() => setIsExpanded(!isExpanded)}
            >
              <ExpandMoreIcon />
            </IconButton>
            <Typography variant='subtitle2' className={classes.name}>{`${category.categoryName} - ${category.status}`}</Typography>
          </div>
        </div>
        <Collapse in={isExpanded} timeout="auto" unmountOnExit>
          {hasContent ? (
            <div className={classes.content}>
              {category.errorMessage && (
                <Typography variant='body2' style={{ color: 'red', margin: 8 }}>
                  {category.errorMessage}
                </Typography>
              )}
              {category.message && (
                <Typography variant='body2' style={{ margin: 8 }}>
                  {category.message}
                </Typography>
              )}
              {category.fieldStatuses && category.fieldStatuses.length > 0 && (
              <DataGrid
                  dataSource={category.fieldStatuses}
                  showRowLines={true}
                  showColumnLines={true}
                  showBorders={true}
                  columnAutoWidth={true}
                  onRowPrepared={handleIsStaleOnRowPrepared} 
                  width='100%'
                >
                  <Column dataField='fieldName' caption='Field' width={300} />
                  <Column dataField='currentValue' caption='Current Value' width={150} cellComponent={LatestValueCell} />
                  <Column dataField='latestValue' caption='Latest Value' width={150} cellComponent={LatestValueCell} />
                  <Column dataField='delta' caption='Delta' width={200} cellComponent={LatestValueCell} />
                  <Column dataField='isDetermined' caption='Determined' width={100} />
                  <Column dataField='isStale' caption='Stale' width={80} />
                  <Column dataField='contributes' caption='Contributes' width={100} />
                  <Column dataField='notes' caption='Notes' />
                  <Column width='auto' />
              </DataGrid>
              )}
            </div>
          ) : <div style={{ margin: 8 }}>No Data</div>}
        </Collapse>
      </div>
    );
  }
	
  return (
    <Dialog onClose={handleClose} open={open} className={classes.dialog} maxWidth={'xl'} fullWidth={true}>
			<DialogTitle>
				Reload Status - {txnOppId}
			</DialogTitle>
			<IconButton className={classes.closeButton} onClick={handleClose}>
				<ClearIcon fontSize='inherit' />
			</IconButton>
      <DialogContent>
        {selectedOpportunityReloadStatusError ? (
          <Typography>{`Error: ${selectedOpportunityReloadStatusError}`}</Typography>
        ) : selectedOpportunityReloadStatusMessage ? (
          <Typography>{selectedOpportunityReloadStatusMessage}</Typography>
        ) : selectedOpportunityReloadStatusData ? (
          <ReloadStatusCategories />
        ) : selectedOpportunityReloadStatusFetchMessage ? (
          <Typography>{selectedOpportunityReloadStatusFetchMessage}</Typography>
        ) : null}
      </DialogContent>

			<DialogActions>
				<></>
			</DialogActions>
		</Dialog>
  )
}

const styles = theme => ({
  dialog: {
    minWidth: '700px',
    minHeight: '150px'
  },
  closeButton: {
    position: 'absolute',
    right: 0,
    top: 0,
    backgroundColor: '#ffffff'
  },
  layerContainer: {
    scrollMargin: '50px',
    marginBottom: 12,
    borderRadius: 4,
    overflow: 'hidden'
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    padding: '2px 4px',
    backgroundColor: '#cbcbcb',
    // borderBottom: '1px solid #b7b7b7',
  },
  headerLeft: {
    display: 'flex',
    alignItems: 'center',
    flexGrow: 1,
    // margin: '8px 0',
    minWidth: 0
  },
  name: {
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap'
  },
  expandButton: {
    transition: 'transform 150ms cubic-bezier(0.4, 0, 0.2, 1) 0ms',
    padding: 2,
    marginRight: 4
  },
  fresh: {
    backgroundColor: '#ccefcc'
  },
  stale: {
    backgroundColor: theme.palette.warning.main
  },
  error: {
    backgroundColor: theme.palette.red.light
  },
  link: {
    background: 'none',
    border: 'none',
    color: 'blue',
    textDecoration: 'underline',
    cursor: 'pointer',
    font: 'inherit',
    padding: 0,
    '&:hover': {
      color: 'darkblue'
    }
  },
  collapseWrapperInner: {
    paddingBottom: 2
  }
});

const mapState = state => ({
  selectedOpportunityReloadStatusError: state.propertyUWState.selectedOpportunityReloadStatusState.data?.[0]?.error,
  selectedOpportunityReloadStatusMessage: state.propertyUWState.selectedOpportunityReloadStatusState.data?.[0]?.message,
  selectedOpportunityReloadStatusFetchMessage: state.propertyUWState.selectedOpportunityReloadStatusState.message,
  selectedOpportunityReloadStatusData: state.propertyUWState.selectedOpportunityReloadStatusState.data?.[0]?.categories,
});
const mapDispatch = dispatch => ({
  appNotify: (message, variant) => dispatch(appNotify(message, variant))
});

export default compose(
  withStyles(styles),
  connect(mapState, mapDispatch)
)(ReloadStatusModal);
